import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { map, Observable } from 'rxjs';
import { Dtc } from '../../../shared/models/dtc/dtc';
import { DtcListValue } from '../../../shared/models/dtc/dtc-list-value';
import { DtcUpdateConfirmationDto } from '../../../shared/models/dtc/dtc-update-confirmation-dto';
import { CreateDtcDto } from '../../../shared/models/dtc/create-dtc-dto';
import { CommonUtils, getPropertyName } from '../../common/common-utils';
import { optionalBoolToString } from './api-helpers/optional-bool-to-string';
import { DtcQuery } from '../../../shared/models/dtc/dtc-query';
import { BaseQuery } from '../../../shared/models/base/base-query';
import { Pagination } from '../../../shared/models/internal/pagination';
import { TranslationService } from '../translation.service';
import { DtcRange } from '../../../shared/models/dtc/dtc-range';
import { ImportReport } from '../../../shared/models/report/import-report';
import { CloneToSwPartNumbers } from '../../../shared/models/sw-part-number/clone-to-sw-part-numbers';
import { PropertyUpdateSynchronisationInfo } from '../../../shared/models/entity-property-info/property-update-synchronisation-info';
import { DtcUpdate } from '../../../shared/models/dtc/dtc-update';
import { PropertyUpdateInfo } from '../../../services/api/base/property-update-info';

@Injectable({
  providedIn: 'root',
})
export class DtcApiService extends PropertyUpdateInfo<Dtc> {
  private commonUtils = CommonUtils;
  private translationService = inject(TranslationService);

  constructor() {
    super(environment.api + 'dtc');
  }

  public getDtc(id: string, languageCode: string | null = null): Observable<Dtc> {
    if (languageCode == null) {
      languageCode = this.translationService.getLanguage();
    }

    return this.http.get<Dtc>(this.baseUrl + '/' + id + '?languageCode=' + languageCode).pipe(
      map((dtc) => {
        dtc.displayId = this.getDtcDisplayId(dtc.number);
        dtc.extendedDisplayId = `DTC ${dtc.displayId}`;
        this.commonUtils.decorateTranslationStateForLanguageCode(dtc);
        if (dtc.swPartNumber) {
          dtc.swPartNumber.displayId = this.commonUtils.getFriendlySwPartNumber(dtc.swPartNumber);
        }
        return dtc;
      })
    );
  }

  public updateDtc(dtc: DtcUpdate): Observable<DtcUpdateConfirmationDto> {
    return this.http.put<DtcUpdateConfirmationDto>(this.baseUrl, dtc);
  }

  public createDtc(createDtcDto: CreateDtcDto): Observable<DtcListValue> {
    return this.http.post<Dtc>(this.baseUrl, createDtcDto).pipe(
      map((dtc) => {
        return {
          id: dtc.id,
          dtc: this.getDtcDisplayId(dtc.number),
          // TODO: translationState?
        };
      })
    );
  }

  public getDtcs(
    query: DtcQuery,
    languageCodeTranslation: string | null = null,
    languageCodeTranslationsState: string | null = null
  ): Observable<Pagination<Dtc>> {
    return this.searchDtcs(query, false, languageCodeTranslation, languageCodeTranslationsState);
  }

  public setRemoveFlag(id: string): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/softdelete/' + id, null);
  }

  public downloadDtcCsvFile(): Observable<Blob> {
    return this.http.get(this.baseUrl + '/download-csv', {
      responseType: 'blob',
    });
  }

  public getRecycledDtcs(
    query: DtcQuery,
    languageCodeTranslation: string | null = null,
    languageCodeTranslationsState: string | null = null
  ): Observable<Pagination<Dtc>> {
    return this.searchDtcs(query, true, languageCodeTranslation, languageCodeTranslationsState);
  }

  public getSignificantVersions(dtcNumber: string): Observable<DtcRange[]> {
    const queryParams: Record<string, string> = { fullDtcNumber: dtcNumber };

    return this.http
      .get<DtcRange[]>(this.baseUrl + '/significant-versions?' + new URLSearchParams(queryParams).toString(), {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const dtcRanges = response.body ?? [];
          const mappedDtcRanges = dtcRanges.map((dtcRange) => {
            dtcRange.from!.displayId = this.getDtcDisplayId(dtcRange.from?.number);
            dtcRange.to!.displayId = this.getDtcDisplayId(dtcRange.to?.number);
            return dtcRange;
          });
          return mappedDtcRanges;
        })
      );
  }

  public restoreDtc(id: string): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/restore/' + id, null);
  }

  public deleteDtc(id: string): Observable<void> {
    return this.http.delete<void>(this.baseUrl + '/' + id);
  }

  public importDtcFromExcel(nwNumber: string, majorVersion: string, excelFile?: File): Observable<ImportReport> {
    const formData = new FormData();
    if (excelFile) {
      formData.append('excelFile', excelFile);
    }
    return this.http.post<ImportReport>(this.baseUrl + `/import/${nwNumber}-${majorVersion}`, formData);
  }

  public searchDtcs(
    query: DtcQuery,
    softDeletedScope: boolean,
    languageCodeTranslation: string | null = null,
    languageCodeTranslationsState: string | null = null
  ): Observable<Pagination<Dtc>> {
    if (languageCodeTranslation == null) {
      // lists are usually displayed in the default language.
      languageCodeTranslation = TranslationService.defaultLanguageCode();
    }
    if (languageCodeTranslationsState == null) {
      languageCodeTranslationsState = languageCodeTranslation;
    }

    const queryParams: Record<string, string> = {
      swPartNumberId: query.swPartNumberId ?? '',
      errorMessageId: query.errorMessageId ?? '',

      searchPattern: query.searchPattern ?? '',
      searchInValue: optionalBoolToString(query.searchInValue, true),
      searchInGroup: optionalBoolToString(query.searchInGroup, false),
      searchInPriority: optionalBoolToString(query.searchInPriority, false),
      searchInMachineFields: optionalBoolToString(query.searchInMachineFields, false),
      searchInDocumentationFields: optionalBoolToString(query.searchInDocumentationFields, false),
      searchInOtherFields: optionalBoolToString(query.searchInOtherFields, true),
      orderBy: query.orderBy ?? '',
      orderByDescending: query.orderByDescending ? 'true' : 'false',
      languageCode: languageCodeTranslation!,
      translationLanguageCodeForSorting: languageCodeTranslationsState,
    };

    this.updateQueryParamsForDetails(query, queryParams);

    this.updateQueryParamsForCauses(query, queryParams);

    this.updateQueryParamsForImplementationHint(query, queryParams);

    this.updateQueryParamsForRemedy(query, queryParams);

    this.updateQueryParamsForErrorMessage(query, queryParams);

    this.updateQueryParamsForReviewRelatedFilters(query, queryParams);

    this.updateQueryParamsForOther(query, queryParams);

    const url = this.baseUrl + (softDeletedScope ? '/recycled?' : '?');

    return this.http
      .get<Dtc[]>(url + new URLSearchParams(queryParams).toString(), {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const dtcs = response.body ?? [];
          const paginationHeader = response.headers.get(this.xPaginationHeader);

          if (paginationHeader) {
            const pagination = JSON.parse(paginationHeader) as Pagination<Dtc>;
            pagination.items = dtcs.map((dtc) => {
              dtc.displayId = this.getDtcDisplayId(dtc.number);
              dtc.extendedDisplayId = `DTC ${dtc.displayId}`;
              this.commonUtils.decorateTranslationStateForLanguageCode(dtc, languageCodeTranslationsState);

              return dtc;
            });
            return pagination;
          }
          throw new Error('Pagination header not found');
        })
      );
  }

  public override getPropertyUpdateInfo(pendingDtc: Dtc): Observable<PropertyUpdateSynchronisationInfo> {
    return this.http
      .post<PropertyUpdateSynchronisationInfo>(this.baseUrl + '/property-update-info', pendingDtc)
      .pipe(map((propertyUpdateInfo) => this.mapPropertyUpdateInfo(propertyUpdateInfo)));
  }

  public cloneDtcToSwPartNumbers(cloneToSwPartNumbers: CloneToSwPartNumbers): Observable<DtcListValue[]> {
    return this.http.post<Dtc[]>(this.baseUrl + '/clone-to-swpartnumbers', cloneToSwPartNumbers).pipe(
      map((dtcs) => {
        return dtcs.map((dtc) => {
          const dtcValue: DtcListValue = {
            id: dtc.id,
            dtc: this.getDtcDisplayId(dtc.number),
          };
          return dtcValue;
        });
      })
    );
  }

  private updateQueryParamsForCauses(query: DtcQuery, queryParams: Record<string, string>): void {
    if (query.causeInternNotEmpty !== undefined) {
      queryParams[getPropertyName<DtcQuery>('causeInternNotEmpty')] = query.causeInternNotEmpty.toString();
    }
    if (query.causeDealerNotEmpty !== undefined) {
      queryParams[getPropertyName<DtcQuery>('causeDealerNotEmpty')] = query.causeDealerNotEmpty.toString();
    }
    if (query.causeFarmerNotEmpty !== undefined) {
      queryParams[getPropertyName<DtcQuery>('causeFarmerNotEmpty')] = query.causeFarmerNotEmpty.toString();
    }
  }

  private updateQueryParamsForDetails(query: DtcQuery, queryParams: Record<string, string>): void {
    if (query.detailsOnTerminalNotEmpty !== undefined) {
      queryParams[getPropertyName<DtcQuery>('detailsOnTerminalNotEmpty')] = query.detailsOnTerminalNotEmpty.toString();
    }
  }

  private updateQueryParamsForErrorMessage(query: DtcQuery, queryParams: Record<string, string>): void {
    if (query.hasErrorMessage !== undefined) {
      queryParams[getPropertyName<DtcQuery>('hasErrorMessage')] = query.hasErrorMessage.toString();
    }
  }

  private updateQueryParamsForReviewRelatedFilters(query: DtcQuery, queryParams: Record<string, string>): void {
    if (query.isReviewedByAfterSales !== undefined) {
      queryParams[getPropertyName<DtcQuery>('isReviewedByAfterSales')] = query.isReviewedByAfterSales.toString();
    }

    if (query.hasActiveAfterSalesReview !== undefined) {
      queryParams[getPropertyName<DtcQuery>('hasActiveAfterSalesReview')] = query.hasActiveAfterSalesReview.toString();
    }

    if (query.isReviewedByTechnicalOrganisation !== undefined) {
      queryParams[getPropertyName<DtcQuery>('isReviewedByTechnicalOrganisation')] =
        query.isReviewedByTechnicalOrganisation.toString();
    }

    if (query.hasActiveTechnicalOrganisationReview !== undefined) {
      queryParams[getPropertyName<DtcQuery>('hasActiveTechnicalOrganisationReview')] =
        query.hasActiveTechnicalOrganisationReview.toString();
    }
    if (query.mainLanguageTerminalFieldsReviewed !== undefined) {
      queryParams[getPropertyName<DtcQuery>('mainLanguageTerminalFieldsReviewed')] =
        query.mainLanguageTerminalFieldsReviewed.toString();
    }
    if (query.mainLanguageDocumentationFieldsReviewed !== undefined) {
      queryParams[getPropertyName<DtcQuery>('mainLanguageDocumentationFieldsReviewed')] =
        query.mainLanguageDocumentationFieldsReviewed.toString();
    }
  }

  private updateQueryParamsForRemedy(query: DtcQuery, queryParams: Record<string, string>): void {
    if (query.remedyInternNotEmpty !== undefined) {
      queryParams[getPropertyName<DtcQuery>('remedyInternNotEmpty')] = query.remedyInternNotEmpty.toString();
    }
    if (query.remedyDealerNotEmpty !== undefined) {
      queryParams[getPropertyName<DtcQuery>('remedyDealerNotEmpty')] = query.remedyDealerNotEmpty.toString();
    }
    if (query.remedyFarmerNotEmpty !== undefined) {
      queryParams[getPropertyName<DtcQuery>('remedyFarmerNotEmpty')] = query.remedyFarmerNotEmpty.toString();
    }
  }

  private updateQueryParamsForImplementationHint(query: DtcQuery, queryParams: Record<string, string>): void {
    if (query.implementationHintNotEmpty !== undefined) {
      queryParams[getPropertyName<DtcQuery>('implementationHintNotEmpty')] =
        query.implementationHintNotEmpty.toString();
    }
  }

  private updateQueryParamsForOther(query: DtcQuery, queryParams: Record<string, string>): void {
    if (query.translationStatesTerminal && query.translationStatesTerminal.length > 0) {
      queryParams[getPropertyName<DtcQuery>('translationStatesTerminal')] = query.translationStatesTerminal.join(',');
    }

    if (query.translationStatesDocumentation && query.translationStatesDocumentation.length > 0) {
      queryParams[getPropertyName<DtcQuery>('translationStatesDocumentation')] =
        query.translationStatesDocumentation.join(',');
    }

    if (query.mainLanguageTerminalFieldsExportedForReview === true) {
      queryParams[getPropertyName<DtcQuery>('mainLanguageTerminalFieldsExportedForReview')] =
        query.mainLanguageTerminalFieldsExportedForReview.toString();
    }
    if (query.mainLanguageDocumentationFieldsExportedForReview === true) {
      queryParams[getPropertyName<DtcQuery>('mainLanguageDocumentationFieldsExportedForReview')] =
        query.mainLanguageDocumentationFieldsExportedForReview.toString();
    }
    if (query.changesBy !== undefined) {
      queryParams[getPropertyName<DtcQuery>('changesBy')] = query.changesBy;
    }

    if (query.page) {
      queryParams[getPropertyName<BaseQuery>('page')] = query.page.toString();
    }

    if (query.pageSize) {
      queryParams[getPropertyName<BaseQuery>('pageSize')] = query.pageSize.toString();
    }

    if (query.orderBy) {
      queryParams[getPropertyName<BaseQuery>('orderBy')] = query.orderBy;
    }

    if (query.orderByDescending !== undefined) {
      queryParams[getPropertyName<BaseQuery>('orderByDescending')] = query.orderByDescending ? 'true' : 'false';
    }

    if (query.ids) {
      queryParams[getPropertyName<DtcQuery>('ids')] = query.ids;
    }
  }

  private getDtcDisplayId(dtc?: number): string {
    return dtc?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') ?? '';
  }
}
